import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import LeviVoiceSettings from "@/components/settings/LeviVoiceSettings";
import AccountDangerZone from "@/components/settings/AccountDangerZone";
import ChangePasswordCard from "@/components/settings/ChangePasswordCard";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Check, CreditCard, Loader2, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { isNativeShell } from "@/lib/platform";

// Slice 2S · display labels moved to i18n dictionary (account.status_labels.*)
// via t(...) below.  Legacy default kept for read-time safety if a code
// arrives that neither i18n branch knows about.
const STATUS_LABEL_FALLBACK = "—";

// Formats a Stripe UNIX timestamp (seconds) into a locale-safe date
// string; falls back to em-dash when the field is missing/null.
const formatRenewal = (unix) => {
  if (!unix) return "—";
  try {
    const d = new Date(unix * 1000);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "—";
  }
};

const Account = () => {
  const { user, refreshMe, setEntitlement } = useAuth();
  const { t } = useLang();
  const isDemo = !!user?.is_demo;
  const [tiers, setTiers] = useState([]);
  const [sub, setSub] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [polling, setPolling] = useState("");
  const [params, setParams] = useSearchParams();
  const [savingCdl, setSavingCdl] = useState(false);
  const pollRef = useRef(null);

  const [portalLoading, setPortalLoading] = useState(false);

  // Slice 2D3 · Customer-document language preference. Persists on the
  // authenticated-user record via a single-purpose endpoint. Reuses the
  // existing authenticated user state (useAuth + refreshMe) — no new
  // context, no global store, no parallel localStorage source.
  const customer_document_language = user?.customer_document_language || "en";
  const saveCustomerDocLanguage = async (next) => {
    if (savingCdl) return;
    if (next !== "en" && next !== "es") return;
    if (next === customer_document_language) return;
    setSavingCdl(true);
    try {
      await api.put("/auth/customer-document-language", {
        customer_document_language: next,
      });
      await refreshMe();
      toast.success(
        next === "es"
          ? "Idioma de documentos del cliente: Español"
          : "Customer document language: English",
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not save language preference",
      );
    } finally {
      setSavingCdl(false);
    }
  };

  useEffect(() => {
    api.get("/subscriptions/tiers").then((r) => setTiers(r.data));
    api.get("/subscriptions/me").then((r) => setSub(r.data));
  }, [isDemo]);

  useEffect(() => {
    // Roadmap #2 · Strategy A · native builds never initiate Stripe
    // checkout, so the ``?session_id=`` return-polling path must be
    // inert on iOS/Android even if the URL is somehow reached.
    if (isNativeShell()) return;
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const max = 8;
    setPolling("Confirming payment...");
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/v1/checkout/status/${sid}`);
        if (data.subscription_ready) {
          setPolling("");
          toast.success(`Subscription activated: ${data.tier} · ${data.billing_cycle}`);
          const me = await api.get("/subscriptions/me");
          setSub(me.data);
          setEntitlement(null);
          setParams({});
          return;
        }
        if (
          attempts >= max &&
          ["paid", "no_payment_required"].includes(data.payment_status)
        ) {
          setPolling("");
          toast.info("Checkout completed. Subscription confirmation is still processing.");
          setParams({});
          return;
        }
        if (data.status === "expired" || attempts >= max) {
          setPolling("");
          toast.error("Payment was not completed. You can try again.");
          setParams({});
          return;
        }
        pollRef.current = setTimeout(tick, 2000);
      } catch {
        setPolling("");
        setParams({});
      }
    };
    tick();
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("session_id")]);

  useEffect(() => {
    if (params.get("canceled")) {
      toast.info("Checkout canceled — no charge made.");
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("canceled")]);

  const checkout = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      // Slice 2R · call the sole active recurring-subscription endpoint.
      const { data } = await api.post("/stripe/subscription/checkout", {
        tier: tierId,
        billing_cycle: cycle,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setCheckoutLoading("");
    }
  };

  const openPortal = async () => {
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const { data } = await api.post("/stripe/portal", {});
      window.location.href = data.url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not open the billing portal",
      );
      setPortalLoading(false);
    }
  };

  const formatTierLabel = (id) => {
    if (isDemo) return "Demo Contractor Plan";
    const t = tiers.find((x) => x.id === id);
    return t?.name || id || "—";
  };

  return (
    <DashboardLayout title={t("pages.account.title")} subtitle={t("pages.account.subtitle")}>
      <div className="max-w-6xl">
        {polling && (
          <div className="mb-6 jc-card p-4 flex items-center gap-3 border-l-4 border-l-[var(--jc-blue)]" data-testid="checkout-polling">
            <Loader2 className="animate-spin text-[var(--jc-blue)]" size={18} />
            <div className="text-sm font-semibold">{polling}</div>
          </div>
        )}

        {!isDemo && ["pending_checkout", "checkout_pending"].includes(sub?.status) && (
          <div
            className="mb-6 border-l-4 border-l-[var(--jc-blue)] bg-white border-y border-r border-[var(--jc-border)] p-5"
            data-testid="checkout-required-banner"
          >
            <div className="font-display text-xl" data-testid="checkout-required-banner-title">
              Your 7-day trial has not started.
            </div>
            <p className="mt-1 text-sm text-[var(--jc-text-soft)]" data-testid="checkout-required-banner-body">
              {isNativeShell()
                ? "Open JobClose AI in a web browser to choose billing and begin your trial."
                : "Choose monthly or annual billing below, then complete Checkout to begin your one-time trial."}
            </p>
          </div>
        )}

        {/* Slice 2D3 · Customer-document language preference.
            Segmented English | Español selector. One click on either
            segment lands on that exact locale — never derives next
            value from stale state. Active segment is visually
            unmistakable via filled background + white text. Persists
            via PUT /auth/customer-document-language and reconciles
            the authenticated user through refreshMe(). Independent
            of the app-language toggle in the top bar. */}
        <div
          className="jc-card p-5 mb-6"
          data-testid="customer-doc-lang-card"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="overline">{t("account.customer_doc_lang_heading")}</div>
              <p className="text-sm text-[var(--jc-text-soft)] mt-1">
                {t("account.customer_doc_lang_body")}
              </p>
            </div>
            <div
              role="group"
              aria-label="Customer document language"
              className="inline-flex items-center rounded-md border border-[var(--jc-border)] p-0.5 bg-white shrink-0"
            >
              <button
                type="button"
                onClick={() => saveCustomerDocLanguage("en")}
                disabled={savingCdl}
                aria-pressed={customer_document_language === "en"}
                data-testid="customer-doc-lang-toggle-en"
                data-active={customer_document_language === "en" ? "true" : "false"}
                className={
                  "px-3 py-1.5 text-xs font-semibold rounded-[5px] transition " +
                  (customer_document_language === "en"
                    ? "bg-[var(--jc-navy)] text-white"
                    : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")
                }
              >
                English
              </button>
              <button
                type="button"
                onClick={() => saveCustomerDocLanguage("es")}
                disabled={savingCdl}
                aria-pressed={customer_document_language === "es"}
                data-testid="customer-doc-lang-toggle-es"
                data-active={customer_document_language === "es" ? "true" : "false"}
                className={
                  "px-3 py-1.5 text-xs font-semibold rounded-[5px] transition " +
                  (customer_document_language === "es"
                    ? "bg-[var(--jc-navy)] text-white"
                    : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")
                }
              >
                Español
              </button>
            </div>
          </div>
        </div>

        <div className="jc-card p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="current-plan-card">
          <div className="min-w-0">
            <div className="overline">{t("account.current_plan")}</div>
            {sub?.entitlement_type === "internal_owner" ? (
              <>
                <div className="font-display text-2xl mt-1" data-testid="account-internal-owner-plan">
                  Internal Owner Account — $0 Billing
                </div>
                <div className="text-xs text-[var(--jc-text-soft)] mt-1" data-testid="subscription-status-label">
                  Full paid-tier access with no trial expiration and no subscription charge.
                  Business Texting is included; carrier compliance (Twilio TFV) still applies.
                </div>
                {sub?.internal_entitlement?.granted_at && (
                  <div className="text-[11px] text-[var(--jc-text-soft)] mt-1" data-testid="account-internal-owner-envelope">
                    Granted {String(sub.internal_entitlement.granted_at).slice(0, 10)}
                    {sub.internal_entitlement?.reason ? ` · ${sub.internal_entitlement.reason}` : ""}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="font-display text-2xl mt-1">{formatTierLabel(sub?.tier)}</div>
                <div className="text-xs text-[var(--jc-text-soft)] mt-1" data-testid="subscription-status-label">
              {isDemo ? (
                <span className="font-semibold text-[var(--jc-blue)]">Demo account · complimentary access</span>
              ) : (
                <>
                  {t("account.status")}: <span className="font-semibold">{sub?.status ? t(`account.status_labels.${sub.status}`) : STATUS_LABEL_FALLBACK}</span> · {t("account.cycle")}: <span className="font-semibold capitalize">{sub?.billing_cycle}</span>
                </>
              )}
            </div>
              </>
            )}
            {/* Slice 2R · renewal / cancellation summary. Rendered only when
                Stripe has actually provisioned a subscription — legacy
                placeholder rows have no current_period_end. */}
            {!isDemo && sub?.entitlement_type !== "internal_owner" && sub?.stripe_subscription_id && (
              <div className="mt-2 text-xs text-[var(--jc-text-soft)] flex flex-wrap gap-x-4 gap-y-1" data-testid="subscription-renewal-summary">
                <span>
                  {sub?.cancel_at_period_end ? t("account.cancels_on") : t("account.renews_on")}:{" "}
                  <span className="font-semibold" data-testid="subscription-renewal-date">
                    {formatRenewal(sub?.current_period_end)}
                  </span>
                </span>
                {sub?.cancel_at_period_end && (
                  <span className="font-semibold text-[var(--jc-warn, #b45309)]" data-testid="subscription-cancel-flag">
                    {t("account.set_to_cancel")}
                  </span>
                )}
              </div>
            )}
          </div>
          {/* Roadmap #2 · Strategy A · monthly/annual cycle toggle drives
              the Subscribe price and is therefore a purchase-context
              control.  Manage-Billing (Stripe portal) can currently
              expose upgrade / seat-add flows and is therefore treated
              as a purchase entry point until portal behaviour is
              audited separately.  Both are hidden on the native
              (iOS/Android) shell.  Web behaviour unchanged. */}
          {!isNativeShell() && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {!isDemo && (
                <div className="flex items-center bg-[var(--jc-bg)] border border-[var(--jc-border)] rounded-md p-1 text-xs font-semibold">
                  <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly-btn" className={`px-3 py-1.5 rounded ${cycle === "monthly" ? "bg-white shadow" : "text-[var(--jc-text-soft)]"}`}>{t("account.monthly")}</button>
                  <button onClick={() => setCycle("annual")} data-testid="cycle-annual-btn" className={`px-3 py-1.5 rounded ${cycle === "annual" ? "bg-white shadow" : "text-[var(--jc-text-soft)]"}`}>
                    {t("account.annual")} <span className="ml-1 text-[10px] tracking-wide text-[var(--jc-teal)]">{t("account.save_two_months")}</span>
                  </button>
                </div>
              )}
              {/* Slice 2R · Manage Billing.  Only rendered once Stripe has
                  provisioned a customer for this contractor (i.e. after
                  the very first checkout completes). Demo accounts never
                  see this button. */}
              {!isDemo && sub?.stripe_customer_id && (
                <button
                  type="button"
                  onClick={openPortal}
                  disabled={portalLoading}
                  data-testid="manage-billing-btn"
                  className="px-3 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 jc-btn-primary disabled:opacity-60"
                >
                  {portalLoading ? (
                    <><Loader2 className="animate-spin" size={13} /> {t("account.redirecting")}</>
                  ) : (
                    <><CreditCard size={13} /> {t("account.manage_billing")}</>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Current pricing summary. Internal-owner accounts do not see plans. */}
        {sub?.entitlement_type !== "internal_owner" && (
        <div className="jc-card p-5 mb-6" data-testid="available-plans-summary">
          <div className="overline mb-3">{t("account.available_plans")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><div className="font-semibold">JobClose AI</div><div className="text-[var(--jc-text-soft)]">$129/mo or $1,199/yr · 2 full users included · +$20/mo or $240/yr per additional active user · one 7-day trial</div></div>
            <div><div className="font-semibold">Larger Teams / Enterprise</div><div className="text-[var(--jc-text-soft)]">Custom · volume pricing</div></div>
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-3">Additional active users follow the selected billing cycle. No hidden per-feature tiers.</div>
        </div>
        )}

        {sub?.entitlement_type !== "internal_owner" && (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {tiers.map((tier) => {
            const isCustom = tier.custom;
            const price = isCustom
              ? null
              : cycle === "monthly"
              ? tier.monthly_price
              : tier.annual_price;
            // Real subscription lifecycle statuses that indicate the
            // contractor is currently ON this tier — legacy placeholders
            // are still honored for pre-Slice-2R records.
            const activeStatuses = new Set([
              "active", "trialing", "past_due", "active_test_mode",
            ]);
            const isCurrent =
              sub?.tier === tier.id && activeStatuses.has(sub?.status);
            const loading = checkoutLoading === tier.id;

            return (
              <div
                key={tier.id}
                className={`jc-card p-6 relative flex flex-col ${tier.highlighted ? "border-2 border-[var(--jc-blue)] shadow-lg" : ""}`}
                data-testid={`tier-card-${tier.id}`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full bg-[var(--jc-blue)] text-white text-[10px] font-bold tracking-wider">{t("account.most_popular_badge")}</div>
                )}
                <div className="overline">{tier.name}</div>

                <div className="mt-2 flex items-baseline gap-1">
                  {isCustom ? (
                    <span className="font-display text-3xl">Custom</span>
                  ) : (
                    <>
                      <span className="font-display text-4xl" data-testid={`tier-price-${tier.id}`}>${price}</span>
                      <span className="text-sm text-[var(--jc-text-soft)]">/{cycle === "monthly" ? "mo" : "yr"}</span>
                    </>
                  )}
                </div>
                {!isCustom && cycle === "annual" && tier.annual_savings > 0 && (
                  <div className="text-xs text-[var(--jc-teal)] font-semibold mt-1">Save ${tier.annual_savings}/yr vs monthly</div>
                )}
                {!isCustom && tier.setup_fee > 0 && !isDemo && (
                  <div className="text-xs text-[var(--jc-text-soft)] mt-1">{t("account.setup_fee_separate")}</div>
                )}
                {isCustom && (
                  <div className="text-xs text-[var(--jc-text-soft)] mt-1">Tailored to your team size &amp; rollout</div>
                )}

                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-[var(--jc-green)] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>

                {/* Roadmap #2 · Strategy A · purchase-action controls
                    (Subscribe / Enterprise Contact-Sales mailto) are
                    gated per-button on native so factual tier cards
                    above remain visible while no external-purchase
                    path is exposed.  Web behaviour unchanged. */}
                {!isNativeShell() && (
                  isCustom ? (
                    <a
                      href="mailto:sales@jobclose.ai?subject=JobClose%20AI%20Enterprise%20Inquiry"
                      data-testid="select-enterprise-btn"
                      className="w-full mt-5 px-3 py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 jc-btn-primary"
                    >
                      <Mail size={14} /> {t("account.contact_sales")}
                    </a>
                  ) : (
                    <button
                      onClick={() => checkout(tier.id)}
                      disabled={loading || isCurrent}
                      data-testid={`select-${tier.id}-btn`}
                      className={`w-full mt-5 px-3 py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 ${
                        isCurrent ? "border border-[var(--jc-border-strong)] text-[var(--jc-text-soft)]" : tier.highlighted ? "jc-btn-blue" : "jc-btn-primary"
                      } disabled:opacity-60`}
                    >
                      {loading ? (<><Loader2 className="animate-spin" size={14} /> {t("account.redirecting")}</>) : isCurrent ? t("account.current_plan_action") : (isDemo ? t("account.contact_sales") : t("account.subscribe"))}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
        )}

        <div className="mt-6 jc-card p-6" data-testid="account-setup-note">
          <div className="overline mb-3">{t("account.setup_pkgs_heading")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mb-4">
            {t("account.setup_pkgs_body")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
              <div className="font-semibold">{t("account.setup_pkg_basic_name")}</div>
              <div className="font-mono-jc text-lg mt-1">$249</div>
            </div>
            <div className="p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
              <div className="font-semibold">{t("account.setup_pkg_pro_name")}</div>
              <div className="font-mono-jc text-lg mt-1">$399</div>
            </div>
            <div className="p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
              <div className="font-semibold">{t("account.setup_pkg_premium_name")}</div>
              <div className="font-mono-jc text-lg mt-1">{t("account.starting_at_setup")}</div>
            </div>
            <div className="p-4 rounded-lg bg-[var(--jc-bg)] border border-[var(--jc-border)]">
              <div className="font-semibold">{t("account.setup_pkg_custom_name")}</div>
              <div className="font-mono-jc text-lg mt-1">{t("account.quoted_upfront")}</div>
            </div>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--jc-teal)]/10 border border-[var(--jc-teal)]/30 text-xs font-semibold text-[var(--jc-text)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--jc-teal)]" aria-hidden="true" />
            {t("account.no_surprise_fees_chip")}
          </div>
        </div>

        {/* Optional add-on: Website Starter */}
        <div className="mt-4 jc-card p-6" data-testid="account-website-addon">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="overline mb-1">{t("account.website_starter_heading")}</div>
              <div className="font-mono-jc text-sm text-[var(--jc-teal)] mt-1">{t("account.starting_at_setup")}</div>
            </div>
          </div>
          <p className="mt-3 text-sm text-[var(--jc-text-soft)] leading-relaxed">
            {t("account.website_starter_body")}
          </p>
        </div>
      </div>

      {/* Slice LEVI-VOICE-01 · Levi voice preferences */}
      <div className="mt-6">
        <LeviVoiceSettings />
      </div>

      {/* PO 2026-02 · Pre-Mohit polish · Always-visible Change Password
          card. Reuses the pre-existing POST /api/auth/change-password
          endpoint; no backend change. Independent of
          force_password_change (the temporary-password banner on the
          dashboard continues to render for pilot/invited accounts). */}
      <div className="mt-6 max-w-6xl">
        <ChangePasswordCard />
      </div>

      {/* Account Deletion Safety Bundle A · Danger zone
          Renders self-delete / transfer-ownership / delete-company flows
          based on the caller's role and org composition. */}
      <div className="mt-6 max-w-6xl">
        <AccountDangerZone />
      </div>
    </DashboardLayout>
  );
};

export default Account;
